import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Sky } from "@react-three/drei";
import { forwardRef } from 'react';

export interface SkyMeshProperties extends MeshProperties {
  turbidity: number;
  rayleigh: number;
  mieCoefficient: number;
  mieDirectionalG: number;
  elevation: number;
  azimuth: number;
}

export interface SkyMesh extends Mesh<SkyMeshProperties> {
  subtype: 'sky';
  properties: SkyMeshProperties;
}

// Create a reusable Sky component that can be used in both preview and world
const SkyComponent = forwardRef<THREE.Group, { properties: SkyMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { transform, turbidity, rayleigh, mieCoefficient, mieDirectionalG, elevation, azimuth } = properties;

  // In preview mode, use simplified transform
  const position = isPreview ? [0, 0, 0] : [transform.position.x, transform.position.y, transform.position.z];
  const rotation = isPreview ? [0, 0, 0] : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale = isPreview ? [1, 1, 1] : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Calculate sun position from elevation and azimuth
  const phi = THREE.MathUtils.degToRad(90 - elevation);
  const theta = THREE.MathUtils.degToRad(azimuth);
  const sunPosition = new THREE.Vector3();
  sunPosition.setFromSphericalCoords(1, phi, theta);

  return (
    <group
      ref={ref}
      position={position as [number, number, number]}
      rotation={rotation as [number, number, number]}
      scale={scale as [number, number, number]}
    >
      <Sky
        turbidity={turbidity}
        rayleigh={rayleigh}
        mieCoefficient={mieCoefficient}
        mieDirectionalG={mieDirectionalG}
        sunPosition={[sunPosition.x, sunPosition.y, sunPosition.z]}
      />
    </group>
  );
});

export const SkyMeshItem: SkyMesh = {
  id: '',
  name: 'Sky',
  type: 'mesh',
  subtype: 'sky',
  icon: 'Sun',
  properties: {
    turbidity: 10,
    rayleigh: 3,
    mieCoefficient: 0.005,
    mieDirectionalG: 0.7,
    elevation: 2,
    azimuth: 180,
    transform: {
      position: new Vector3(),
      rotation: new THREE.Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.turbidity',
      type: 'number',
      label: 'Turbidity',
      default: 10,
      panel: 'geometry'
    },
    {
      key: 'properties.rayleigh',
      type: 'number',
      label: 'Rayleigh',
      default: 3,
      panel: 'geometry'
    },
    {
      key: 'properties.mieCoefficient',
      type: 'number',
      label: 'Mie Coefficient',
      default: 0.005,
      panel: 'geometry'
    },
    {
      key: 'properties.mieDirectionalG',
      type: 'number',
      label: 'Mie Directional G',
      default: 0.7,
      panel: 'geometry'
    },
    {
      key: 'properties.elevation',
      type: 'number',
      label: 'Sun Elevation',
      default: 2,
      panel: 'geometry'
    },
    {
      key: 'properties.azimuth',
      type: 'number',
      label: 'Sun Azimuth',
      default: 180,
      panel: 'geometry'
    }
  ],
  render: (properties: SkyMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <SkyComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Group>} />;
  }
};
